import type { FC, FocusEvent, ReactNode } from 'react'
import type { Wallpaper } from '@/types'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ArrowUpRight, Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pluralize } from '@/lib/format'
import { Container } from '@/components/generals/container'
import { SearchForm } from '@/components/generals/search-form'

interface HeroProps {
    wallpapers: Array<Wallpaper>
    total: number
    categoryCount: number
    tags: Array<string>
}

const ROTATE_EVERY_MS = 7000

export const Hero: FC<HeroProps> = ({ wallpapers, total, categoryCount, tags }): ReactNode => {
    const count = wallpapers.length
    const [index, setIndex] = useState(0)
    const [previous, setPrevious] = useState<number | null>(null)
    const [paused, setPaused] = useState(false)
    const [hovering, setHovering] = useState(false)
    const [focused, setFocused] = useState(false)
    const [reduceMotion, setReduceMotion] = useState(false)

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)')
        const update = () => setReduceMotion(query.matches)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        if (count < 2 || paused || hovering || focused || reduceMotion) return

        const timer = setTimeout(() => {
            setPrevious(index)
            setIndex((index + 1) % count)
        }, ROTATE_EVERY_MS)

        return () => clearTimeout(timer)
    }, [index, count, paused, hovering, focused, reduceMotion])

    const show = (next: number) => {
        if (next === index) return

        setPrevious(index)
        setIndex(next)
    }

    const onBlur = (event: FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false)
    }

    const current = wallpapers[index]
    const upcoming = count > 1 ? (index + 1) % count : null

    return (
        <section
            id='hero'
            aria-labelledby='hero-title'
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onFocus={() => setFocused(true)}
            onBlur={onBlur}
            className='relative isolate flex min-h-[92svh] items-end overflow-hidden bg-surface pt-28 pb-10 sm:pb-14'
        >
            <div aria-hidden='true' className='absolute inset-0 -z-20'>
                {wallpapers.map((wallpaper, position) => {
                    const visible = position === index

                    if (!visible && position !== previous && position !== upcoming) return null

                    return (
                        <div
                            key={wallpaper.id}
                            className={cn(
                                'absolute inset-0 transition-opacity duration-[1400ms] ease-out motion-reduce:transition-none',
                                visible ? 'opacity-100' : 'opacity-0'
                            )}
                        >
                            <div className={cn('absolute inset-0', visible && 'animate-ken-burns')}>
                                <Image
                                    src={wallpaper.image}
                                    alt=''
                                    fill
                                    sizes='100vw'
                                    preload={position === 0}
                                    className='object-cover'
                                />
                            </div>
                        </div>
                    )
                })}
            </div>

            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-t from-background via-background/70 to-background/10' />
            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-r from-background/80 via-background/25 to-transparent' />

            <Container className='relative grid gap-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end'>
                <div className='max-w-3xl'>
                    {total > 0 && (
                        <p className='inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-3 py-1 text-xs font-medium text-white/85 backdrop-blur-md'>
                            <span aria-hidden='true' className='size-1.5 rounded-full bg-brand' />
                            <span>
                                <span>{total}</span>
                                {' free '}
                                {pluralize(total, 'wallpaper')}
                                {categoryCount > 0 && (
                                    <>
                                        {' in '}
                                        <span>{categoryCount}</span>
                                        {' '}
                                        {pluralize(categoryCount, 'category', 'categories')}
                                    </>
                                )}
                            </span>
                        </p>
                    )}

                    <h1
                        id='hero-title'
                        className='mt-5 font-heading text-[2.9rem] leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-[5.5rem]'
                    >
                        Wallpapers worth
                        {' '}
                        <em className='text-brand'>staring at.</em>
                    </h1>

                    <p className='mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg'>
                        Hand-picked backgrounds for your desktop and phone, sorted by mood and color. Find one you love and download it in a click.
                    </p>

                    <SearchForm
                        size='lg'
                        className='mt-8 max-w-xl'
                        placeholder="Try “pastel” or “neon”"
                    />

                    {tags.length > 0 && (
                        <div className='mt-4 flex flex-wrap items-center gap-2 text-sm'>
                            <span className='text-white/60'>
                                Popular:
                            </span>

                            {tags.map(tag => (
                                <Link
                                    key={tag}
                                    href={`/wallpapers?q=${encodeURIComponent(tag)}`}
                                    className='rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-white/85 backdrop-blur-md transition hover:bg-white/15 focus-visible:ring-3 focus-visible:ring-ring/60 focus-visible:outline-none'
                                >
                                    {tag}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {current && (
                    <div className='flex flex-wrap items-center gap-3 lg:flex-col lg:items-end'>
                        <Link
                            href={`/wallpapers/${current.slug}`}
                            className='group flex items-center gap-3 rounded-2xl border border-white/15 bg-black/35 p-2 pr-4 backdrop-blur-md transition hover:bg-black/50 focus-visible:ring-3 focus-visible:ring-ring/60 focus-visible:outline-none'
                        >
                            <span className='relative size-12 shrink-0 overflow-hidden rounded-xl'>
                                <Image src={current.image} alt='' fill sizes='48px' className='object-cover' />
                            </span>

                            <span className='min-w-0'>
                                <span className='block text-[0.68rem] font-semibold tracking-[0.14em] text-white/60 uppercase'>
                                    Now showing
                                </span>

                                <span
                                    className='block max-w-[14rem] truncate text-sm font-medium text-white'
                                    data-mdlfy-cms={`wallpapers:${current.id}:title`}
                                >
                                    {current.title}
                                </span>
                            </span>

                            <ArrowUpRight aria-hidden='true' className='size-4 text-white/70 transition group-hover:text-white' />
                        </Link>

                        {count > 1 && (
                            <div className='flex items-center gap-2'>
                                <div role='group' aria-label='Spotlight wallpapers' className='flex items-center gap-1'>
                                    {wallpapers.map((wallpaper, position) => (
                                        <button
                                            key={wallpaper.id}
                                            type='button'
                                            onClick={() => show(position)}
                                            aria-label={`Show ${wallpaper.title}`}
                                            aria-pressed={position === index}
                                            className='group/dot inline-flex h-8 cursor-pointer items-center rounded-full px-1 focus-visible:ring-3 focus-visible:ring-ring/60 focus-visible:outline-none'
                                        >
                                            <span
                                                className={cn(
                                                    'block h-1.5 rounded-full transition-all duration-300',
                                                    position === index ? 'w-7 bg-white' : 'w-3 bg-white/40 group-hover/dot:bg-white/70'
                                                )}
                                            />
                                        </button>
                                    ))}
                                </div>

                                {!reduceMotion && (
                                    <button
                                        type='button'
                                        onClick={() => setPaused(value => !value)}
                                        className='inline-flex size-8 cursor-pointer items-center justify-center rounded-full border border-white/15 bg-black/30 text-white/85 backdrop-blur-md transition hover:bg-black/50 focus-visible:ring-3 focus-visible:ring-ring/60 focus-visible:outline-none'
                                    >
                                        {paused ? <Play aria-hidden='true' className='size-3.5' /> : <Pause aria-hidden='true' className='size-3.5' />}
                                        <span className='sr-only'>{paused ? 'Play spotlight' : 'Pause spotlight'}</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Container>
        </section>
    )
}
